import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { useToast } from 'vue-toastification';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import { isPlannedRangeComplete } from '@/components/shipment/form/utils/shipment-form-planned-range-sync';
import {
  flattenLaravelValidationErrors,
  getLaravelValidationErrorsFromAxios,
} from '@/utils/laravel-validation-errors';

export type ShipmentFormGeneralErrors = {
  name: string;
  plannedRange: string;
  device: string;
};

export function emptyGeneralErrors(): ShipmentFormGeneralErrors {
  return { name: '', plannedRange: '', device: '' };
}

export function useShipmentFormValidation(options: {
  generalErrors: Ref<ShipmentFormGeneralErrors>;
}) {
  const toast = useToast();
  const formStore = useShipmentFormStore();
  const { generalName, generalCarrier, generalNotes, generalAssetValueCents, generalPlannedRange } =
    storeToRefs(formStore);

  function validateGeneralFields(input: {
    requirePlannedRange?: boolean;
    requireName?: boolean;
  } = {}): boolean {
    const requireName = input.requireName !== false;
    const requirePlannedRange = input.requirePlannedRange === true;
    const errors = emptyGeneralErrors();

    if (requireName && !generalName.value?.trim()) {
      errors.name = 'Reference ID is required';
    }
    if (
      requirePlannedRange &&
      !isPlannedRangeComplete(generalPlannedRange.value)
    ) {
      errors.plannedRange = 'Planned pickup and delivery are required';
    }

    options.generalErrors.value = errors;
    return !errors.name && !errors.plannedRange;
  }

  function validateDeviceSelection(
    devices: readonly { value: number; label: string }[],
  ): boolean {
    const errors = emptyGeneralErrors();
    const device = devices[0];
    if (!device) {
      errors.device = 'Tracker is required';
    } else if (typeof device.label !== 'string') {
      errors.device = 'Tracker label is invalid';
    } else if (typeof device.value !== 'number') {
      errors.device = 'Tracker id is invalid';
    }
    options.generalErrors.value = errors;
    return !errors.device;
  }

  function assertNotRoutingBusy(isRoutingBusy: () => boolean, message: string) {
    if (!isRoutingBusy()) return true;
    toast.error(message);
    return false;
  }

  function normalizeMetadataPayload() {
    return {
      name: generalName.value.trim(),
      carrier:
        generalCarrier.value?.trim() === ''
          ? null
          : generalCarrier.value?.trim() ?? null,
      notes:
        generalNotes.value?.trim() === ''
          ? null
          : generalNotes.value ?? null,
      asset_value: generalAssetValueCents.value,
    };
  }

  function handleFormApiError(err: unknown, fallback: string) {
    const laravel = getLaravelValidationErrorsFromAxios(err);
    if (laravel) {
      formStore.setServerFieldErrors(
        flattenLaravelValidationErrors(laravel),
      );
    }
    const message =
      (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ??
      (err as Error)?.message ??
      fallback;
    toast.error(message);
  }

  return {
    validateGeneralFields,
    validateDeviceSelection,
    assertNotRoutingBusy,
    normalizeMetadataPayload,
    handleFormApiError,
    isPlannedRangeComplete: () =>
      isPlannedRangeComplete(generalPlannedRange.value),
  };
}
